import { BankConnectivityProvider } from '@40seas/entities/bank-connectivity';
import { Buyer } from '@40seas/entities/buyers';
import { EnumSchema, Executor, FromSchema } from '@40seas/entities/common';
import { BaseEntity, WithBaseEntitySchema } from '@40seas/models';
import { z } from 'zod';
import { FinancialConnectionRecovery } from './financial-connection.recovery.enum';
import { FinancialConnectionStatus } from './financial-connection.status.enum';
import {
    FinancialConnectionProviderFieldsGocardless,
    FinancialConnectionProviderFieldsGocardlessSchema,
} from './provider-fields/gocardless/financial-connection.provider-fields.gocardless.entity';
import {
    FinancialConnectionProviderFieldsPlaid,
    FinancialConnectionProviderFieldsPlaidSchema,
} from './provider-fields/plaid/financial-connection.provider-fields.plaid.entity';
import {
    FinancialConnectionProviderFieldsPersonalGuarantee,
    FinancialConnectionProviderFieldsPersonalGuaranteeSchema,
} from './provider-fields/personalGuarantee/financial-connection.provider-fields.personal-guarantee.entity';

export enum FinancialConnectionProviderType {
    Stripe = BankConnectivityProvider.Stripe,
    GoCardLess = BankConnectivityProvider.GoCardLess,
    Plaid = BankConnectivityProvider.Plaid,
    Codat = BankConnectivityProvider.Codat,
    Rutter = BankConnectivityProvider.Rutter,
    Documents = BankConnectivityProvider.Documents,
    OrdersHistory = BankConnectivityProvider.OrdersHistory,
    ExternalFiles = BankConnectivityProvider.ExternalFiles,
    PersonalGuarantee = BankConnectivityProvider.PersonalGuarantee,
}

export const FinancialConnectionSchema = WithBaseEntitySchema(
    z.object({
        buyer: z.string().or(Buyer['schema']),
        providerType: EnumSchema(FinancialConnectionProviderType),
        providerId: z.string().optional(),
        last4: z.string().nullable().optional(),
        platform: z.string().nullable().optional(),
        institutionName: z.string().optional(),
        lastRetrievedTransactionId: z.string().optional(),
        providerFields: FinancialConnectionProviderFieldsPlaidSchema.or(FinancialConnectionProviderFieldsGocardlessSchema)
            .or(FinancialConnectionProviderFieldsPersonalGuaranteeSchema)
            .optional(),
        status: EnumSchema(FinancialConnectionStatus).optional(),
        recovery: EnumSchema(FinancialConnectionRecovery).nullable().optional(),
        expirationDate: z.string().nullish(),
    }),
).strict();

export class FinancialConnection extends FromSchema(FinancialConnectionSchema) implements BaseEntity {
    id: string;
    buyer: string | Buyer;
    providerId?: string;
    providerType: FinancialConnectionProviderType;
    last4?: string;
    platform?: string;
    institutionName?: string;
    lastRetrievedTransactionId?: string;
    createdAt: Date;
    updatedAt: Date;
    createdBy?: Executor;
    tenantId: string;
    seller?: string;
    expirationDate?: string;
    providerFields?:
        | FinancialConnectionProviderFieldsGocardless
        | FinancialConnectionProviderFieldsPlaid
        | FinancialConnectionProviderFieldsPersonalGuarantee;
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
    status: FinancialConnectionStatus;
    recovery?: FinancialConnectionRecovery;
}
